import type { ReactNode } from 'react';
import { forwardRef } from 'react';
import {
  ListItem,
  type ListItemProps,
  ListItemText,
  ListItemIcon,
  type SxProps,
  type Theme,
} from '@mui/material';
import { Box } from '../../Layout/Box';

export interface ContentListItemProps extends Omit<ListItemProps, 'children'> {
  /**
   * The header content of the list item.
   * Can be a string or any React node (e.g., Typography component).
   */
  header?: string | ReactNode;
  /**
   * The primary content of the list item.
   * Can be a string or any React node (e.g., Typography component).
   */
  primary: string | ReactNode;
  /**
   * The secondary content of the list item (optional).
   * Can be a string or any React node (e.g., Typography component).
   */
  secondary?: string | ReactNode;
  /**
   * The footer content of the list item (optional).
   * Can be a string or any React node (e.g., Typography component).
   */
  footer?: string | ReactNode;
  /**
   * Optional action element displayed on the right side (e.g., IconButton, Button).
   */
  action?: ReactNode;
  /**
   * System prop to customize the component's styles.
   * Will be merged with default styles.
   */
  sx?: SxProps<Theme>;
}

// Use pixel values for borderRadius so they are not multiplied by theme.shape.borderRadius
// (theme has shape.borderRadius: 0, so numeric values would always resolve to 0).
const defaultStyles: SxProps<Theme> = {
  border: 1,
  borderColor: 'grey.500',
  borderRadius: '4px',
  mb: 1,
  py: 0.2,
  pr: 0,
  pl: 1,
  boxShadow: 3,
  display: 'flex',
  alignItems: 'flex-start',
};

// Width of the action icon slot: CircularIconButton "md" (40 px) + ListItemIcon gutter (11 px)
const ACTION_SLOT_WIDTH = '51px';

/**
 * ContentListItem – A styled list item for structured content (header, primary, secondary, footer, action).
 *
 * @example
 * ```tsx
 * <ContentListItem
 *   primary="Task Title"
 *   secondary="Task description goes here"
 *   action={<IconButton><CheckIcon /></IconButton>}
 * />
 * ```
 *
 * @example
 * ```tsx
 * <ContentListItem
 *   primary={<Typography variant="h6">Custom Title</Typography>}
 *   secondary={<Typography variant="body2">Custom description</Typography>}
 *   action={<Button>Action</Button>}
 *   sx={{ backgroundColor: 'primary.light' }}
 * />
 * ```
 */
export const ContentListItem = forwardRef<HTMLLIElement, ContentListItemProps>(
  ({ header, primary, secondary, footer, action, sx, ...other }, ref) => (
    <ListItem {...other} ref={ref} sx={[defaultStyles, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: action ? `calc(100% - ${ACTION_SLOT_WIDTH})` : '100%',
        }}
      >
        {header && <ListItemText primary={header} />}
        <ListItemText primary={primary} secondary={secondary} />
        {footer && <ListItemText primary={footer} />}
      </Box>
      {action && <ListItemIcon sx={{ minWidth: 'auto' }}>{action}</ListItemIcon>}
    </ListItem>
  )
);

ContentListItem.displayName = 'ContentListItem';
